import React, { useState } from 'react'
import { IEvent, WorkflowStatus } from '../../store/events/interfaces'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { checkRegIntegrity } from '../../functions/events/EventIntegrity'
import { addToast } from '../../store/misc/misc'
import { addRegistration, removeRegistration } from '../../store/registrations/registrations'
import { isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ToastType } from '../../store/misc/interfaces'
import { IProgram } from '../../store/program/interfaces'
import { IconName } from '@fortawesome/free-regular-svg-icons'
import { ExternalRegistrationModal } from '../events/ExternalRegistrationModal'
import { useNavigate, useLocation } from 'react-router-dom'
import { stages } from '../../routes'

const DeniedReason = (props: {
  icon: IconName
  reason: string
  tooltip?: string
  eventId?: number
}) => {
  const {icon, reason, tooltip, eventId} = props
  const content = (
    <>
      <FontAwesomeIcon icon={icon} role="presentation" />
      &nbsp;{reason}
    </>
  )

  if (tooltip) {
    return (
      <OverlayTrigger
        placement='top'
        overlay={
          <Tooltip id={`${eventId}-denied-tooltip`}>{tooltip}</Tooltip>
        }
        trigger={['hover', 'focus']}
      >
        <span>{content}</span>
      </OverlayTrigger>
    )
  }

  return content
}

export const checkEventRegistrationDenied = (program: IProgram, event: IEvent): JSX.Element | boolean => {
  const now = new Date()
  if (program.start_registration_date === null) {
    return <DeniedReason icon="calendar-times" reason="Kein Anmeldestart festgelegt" />
  }

  const isEventFull = event && (event.max_participants !== null && event.max_participants !== 0) && (event.registration_statistics.registration_amount_yes >= event.max_participants && event.registration_statistics.is_waiting_list_full)
  if (isEventFull) {
    return <DeniedReason icon="ban" reason="Voll" />
  }

  if (event.canceled) {
    return <DeniedReason icon="ban" reason="Abgesagt" />
  }

  // External registration is now handled via modal, so no longer "denied"
  if (event.has_external_registration) {
    return false
  }

  const isOver: boolean = Boolean(event?.end) && new Date(event?.end) < new Date()
  if (isOver) {
    return <DeniedReason icon="calendar-times" reason="Vorbei" />
  }

  // Only create Date object if we have a valid date string
  const firstRegDate = event.first_registration_date || program.start_registration_date
  const first_registration_date = firstRegDate ? new Date(firstRegDate) : null

  const programLastRegDate = program.last_registration_date ? new Date(program.last_registration_date) : null;
  const eventLastRegDate = event.last_registration_date ? new Date(event.last_registration_date) : null;

  const last_registration_date = programLastRegDate && eventLastRegDate
    ? new Date(Math.min(programLastRegDate.getTime(), eventLastRegDate.getTime()))
    : programLastRegDate || eventLastRegDate;

  if (first_registration_date && now < first_registration_date) {
    return (
      <DeniedReason
        icon="calendar-times"
        reason={`Ab dem ${first_registration_date.toLocaleDateString('de-DE')}`}
      />
    )
  }

  if (last_registration_date && now > last_registration_date) {
    return <DeniedReason
      icon="calendar-times"
      reason="Anmeldefrist vorbei"
    />
  }

  return false
}


export const RegisterButton = (props: {
  editable: boolean
  event: IEvent
  extraClasses?: string
  inDetailModal?: boolean
}) => {
  const { event, extraClasses, editable, inDetailModal } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)
  const { loadedViaCode } = useSelector((state: RootState) => state.misc)
  const { data: program } = useSelector((state: RootState) => state.program)
  const { list: regs } = useSelector((state: RootState) => state.registrations)
  const {testMode } = useSelector((state: RootState) => state.institution)

  const [showExternalModal, setShowExternalModal] = useState(false)

  const { originalRegistrationIds } = useSelector(
    (state: RootState) => state.participant.data,
  )
  const isOriginalReg = originalRegistrationIds?.includes(event?.event_id)

  const isEventRegistrationCurrentlyDenied = checkEventRegistrationDenied(program, event)

  const parentStage = stages.find(stage =>
    location.pathname.includes(stage.path),
  )

  const isEventWaiting = (event.registration_statistics.is_max_participants_full && !event.registration_statistics.is_waiting_list_full)

  const hasRegistration = regs.some(
    reg => reg.event.event_id === event.event_id,
  )

  if (!(editable || program.is_participant_registration_allowed || testMode)) {
    return <></>
  }

  const buttonDisabled = (
    event.canceled ||
    (loadedViaCode && hasRegistration && isOriginalReg) ||
    (!!isEventRegistrationCurrentlyDenied && !event.has_external_registration) ||
    (program.use_workflow && (event.workflow_status !== WorkflowStatus.INSTITUTION_OK)) ||
    (inDetailModal && event.has_external_registration && !event.external_registration_link)
  )

  return (
    <>
      <Button
        variant={hasRegistration ? 'success' : 'info'}
        className={`${isTabletOrMobile ? 'mobile-btn' : 'w-100 h-100'} d-flex align-items-center justify-content-center ${extraClasses}`}
        disabled={
          testMode
            ? false
            : buttonDisabled
        }
        onClick={() => {
        if (event.has_external_registration) {
          // if we are in the detail modal and the event has an external registration link, open it in a new tab
          if (inDetailModal) {
            window.open(event.external_registration_link, '_blank')
            return
          }
          // If description is set, show external modal
          if (event.external_registration_description_html && event.external_registration_description_html.trim()) {
            setShowExternalModal(true)
            return
          }
          // If no description, open detail modal (navigate to event detail page)
          if (parentStage) {
            navigate(`${parentStage.path}/${event.event_id}?highlight=external-registration`)
            return
          }
        }
        if (regs.some((reg) => reg.event.event_id === event.event_id)) {
          dispatch(removeRegistration(event.event_id))
        } else {
          const issueToast = checkRegIntegrity(event, regs, program)
          if (issueToast) {
            dispatch(addToast(issueToast))
            if (
              issueToast.type === ToastType.Warning ||
              issueToast.type === ToastType.Info
            ) {
              dispatch(addRegistration(event))
            }
          } else {
            dispatch(addRegistration(event))
          }
        }
      }}
    >
      {event.has_external_registration ? (
        <span>
          <FontAwesomeIcon icon="external-link-square-alt" role="presentation" />
          {inDetailModal ? " Zur Externen Anmeldung" : " Externe Anmeldung"}
        </span>
      ) : !!isEventRegistrationCurrentlyDenied ? (
          <>{isEventRegistrationCurrentlyDenied}</>
      ) : loadedViaCode && hasRegistration && isOriginalReg ? (
        <>
          <FontAwesomeIcon icon="check-circle" role="presentation" />
          &nbsp;Bereits registriert
        </>
      ) : hasRegistration ? (
        <>
          <FontAwesomeIcon icon="minus-circle" role="presentation" />
          &nbsp;Abwählen
        </>
      ) : isEventWaiting ? (
        <>
          <FontAwesomeIcon icon="hourglass" role="presentation" />
          &nbsp;Warteliste
        </>
      ) : (
        <>
          <FontAwesomeIcon icon="plus-circle" />
          &nbsp;Auswählen
        </>
      )}
      {testMode && buttonDisabled ?
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id={`${event.id}-disabled-info`}>Diese Veranstaltung wäre für Teilnehmer im Moment nicht buchbar.</Tooltip>
          }
          trigger={['hover', 'focus']}
        >
          <FontAwesomeIcon icon={"warning"} style={{marginLeft: "5px"}}></FontAwesomeIcon>
        </OverlayTrigger>: ""}
      </Button>
      {showExternalModal && (
        <ExternalRegistrationModal
          event={event}
          show={showExternalModal}
          onHide={() => setShowExternalModal(false)}
        />
      )}
    </>
  )
}
